import { useState } from "react";
import { Link } from "react-router-dom";
import {
  useDeletePackage,
  usePackageDetail,
  usePackages,
  useYankPackage,
  type SkillState,
} from "../hooks/usePackages.js";
import { formatRelative } from "../lib/format.js";
import { PublishSkillModal } from "../components/PublishSkillModal.js";
import { EditSkillModal } from "../components/EditSkillModal.js";
import { ManageSkillModal } from "../components/ManageSkillModal.js";
import { avatarColor, avatarInitial } from "../lib/avatar-color.js";
import { getCurrentAdmin } from "../lib/auth.js";

export function PackagesPage() {
  const me = getCurrentAdmin();
  const { data, isLoading, error } = usePackages();
  const yank = useYankPackage();
  const del = useDeletePackage();

  const [q, setQ] = useState("");
  const [channel, setChannel] = useState<"all" | "official" | "community" | "private">(
    "all",
  );
  const [status, setStatus] = useState<"all" | "live" | "yanked">("all");
  const [publishOpen, setPublishOpen] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState<string | null>(null);
  const [editTarget, setEditTarget] = useState<string | null>(null);
  const [manageTarget, setManageTarget] = useState<string | null>(null);

  const items = (data ?? []).filter((p) => {
    if (q && !p.name.toLowerCase().includes(q.toLowerCase())) return false;
    if (channel !== "all" && p.channel !== channel) return false;
    if (status === "live" && p.yanked === 1) return false;
    if (status === "yanked" && p.yanked === 0) return false;
    return true;
  });

  return (
    <div>
      {/* 页头 */}
      <div style={{ display: "flex", alignItems: "flex-end", gap: 16 }}>
        <div style={{ flex: 1 }}>
          <h1 className="page-title">包管理</h1>
          <p className="page-sub">
            管理所有 hub 上架的 skill / plugin · 共 {data?.length ?? 0} 个
          </p>
        </div>
        <button className="btn btn-primary" onClick={() => setPublishOpen(true)}>
          + 发布新 skill
        </button>
      </div>

      {/* 筛选栏 */}
      <div
        className="card"
        style={{
          display: "flex",
          gap: 10,
          padding: 10,
          marginTop: 20,
          marginBottom: 16,
          alignItems: "center",
          flexWrap: "wrap",
        }}
      >
        <input
          type="search"
          value={q}
          onChange={(e) => setQ(e.target.value)}
          placeholder="🔍  搜索包名..."
          style={{ flex: 1, minWidth: 200, maxWidth: 320, height: 32 }}
        />
        <select
          value={channel}
          onChange={(e) => setChannel(e.target.value as typeof channel)}
          style={{ width: 140, height: 32 }}
        >
          <option value="all">全部频道</option>
          <option value="official">官方</option>
          <option value="community">社区</option>
          <option value="private">私有</option>
        </select>
        <select
          value={status}
          onChange={(e) => setStatus(e.target.value as typeof status)}
          style={{ width: 130, height: 32 }}
        >
          <option value="all">全部状态</option>
          <option value="live">上架中</option>
          <option value="yanked">已下架</option>
        </select>
        <span style={{ marginLeft: "auto", fontSize: 12, color: "var(--text-muted)" }}>
          {items.length} / {data?.length ?? 0}
        </span>
      </div>

      {publishOpen && <PublishSkillModal onClose={() => setPublishOpen(false)} />}
      {deleteTarget && (
        <DeleteConfirmModal
          name={deleteTarget}
          busy={del.isPending}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={async () => {
            await del.mutateAsync(deleteTarget);
            setDeleteTarget(null);
          }}
        />
      )}
      {editTarget && (
        <EditSkillLoader name={editTarget} onClose={() => setEditTarget(null)} />
      )}
      {manageTarget && (
        <ManageSkillModal
          pkg={items.find((p) => p.name === manageTarget) ?? data!.find((p) => p.name === manageTarget)!}
          onClose={() => setManageTarget(null)}
        />
      )}

      {isLoading && <div className="empty">加载中…</div>}
      {error && <div className="err">{(error as Error).message}</div>}

      {!isLoading && items.length === 0 && !error ? (
        <div className="card empty">
          {data && data.length === 0 ? (
            <>
              <div style={{ fontSize: 28, marginBottom: 8 }}>📦</div>
              <div style={{ fontSize: 14, fontWeight: 500, color: "var(--text)" }}>
                还没有包
              </div>
              <div style={{ marginTop: 4 }}>
                点右上角 "+ 发布新 skill",上传一个 zip 发布第一个
              </div>
            </>
          ) : (
            "没有匹配筛选条件的包"
          )}
        </div>
      ) : items.length > 0 ? (
        <table className="table">
          <thead>
            <tr>
              <th>包</th>
              <th style={{ width: 80 }}>类型</th>
              <th style={{ width: 100 }}>频道</th>
              <th style={{ width: 90 }}>最新版</th>
              <th style={{ width: 70 }}>下载</th>
              <th style={{ width: 120 }}>更新于</th>
              <th style={{ width: 80 }}>上架</th>
              <th style={{ width: 130 }}>管控</th>
              <th style={{ width: 320 }}>操作</th>
            </tr>
          </thead>
          <tbody>
            {items.map((p) => (
              <tr key={p.name}>
                <td>
                  <Link
                    to={`/packages/${encodeURIComponent(p.name)}`}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 10,
                      color: "inherit",
                      textDecoration: "none",
                    }}
                  >
                    <div
                      className="avatar"
                      style={{
                        width: 32,
                        height: 32,
                        fontSize: 13,
                        borderRadius: 8,
                        background: avatarColor(p.name),
                      }}
                    >
                      {avatarInitial(p.name)}
                    </div>
                    <code style={{ fontWeight: 500, fontSize: 13 }}>{p.name}</code>
                  </Link>
                </td>
                <td>
                  <span className="pill pill-neutral">{p.family}</span>
                </td>
                <td>
                  <ChannelBadge channel={p.channel} />
                </td>
                <td>
                  <span className="mono" style={{ fontSize: 12 }}>
                    v{p.latest_version ?? "—"}
                  </span>
                </td>
                <td>{p.download_count}</td>
                <td style={{ fontSize: 12, color: "var(--text-muted)" }}>
                  {formatRelative(p.updated_at)}
                </td>
                <td>
                  {p.yanked ? (
                    <span className="pill pill-danger">已下架</span>
                  ) : (
                    <span className="pill pill-success">上架中</span>
                  )}
                </td>
                <td>
                  <ManagedBadge managed={p.managed === 1} state={p.latest_state} />
                </td>
                <td>
                  <div style={{ display: "flex", gap: 4 }}>
                    <Link
                      to={`/packages/${encodeURIComponent(p.name)}`}
                      className="btn btn-ghost btn-xs"
                      style={{ color: "var(--primary)", textDecoration: "none" }}
                    >
                      详情
                    </Link>
                    <button
                      className="btn btn-ghost btn-xs"
                      onClick={() => setEditTarget(p.name)}
                      title="编辑 displayName / 描述 / tags / channel"
                    >
                      ✎ 编辑
                    </button>
                    <button
                      className="btn btn-ghost btn-xs"
                      onClick={() => setManageTarget(p.name)}
                      title="纳入管控 / 切换状态(停用/下架/弃用)"
                    >
                      ⚙ 管控
                    </button>
                    <button
                      className={
                        p.yanked
                          ? "btn btn-secondary btn-xs"
                          : "btn btn-danger btn-xs"
                      }
                      onClick={() =>
                        yank.mutate({ name: p.name, yank: p.yanked === 0 })
                      }
                      disabled={yank.isPending}
                    >
                      {p.yanked ? "上架" : "下架"}
                    </button>
                    {me?.role === "owner" && (
                      <button
                        className="btn btn-danger btn-xs"
                        onClick={() => setDeleteTarget(p.name)}
                        title="彻底删除包 + 所有版本 + R2 文件,不可恢复"
                      >
                        🗑 删除
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}
    </div>
  );
}

/**
 * 列表页的"编辑"入口:列表只有 PackageRow,EditSkillModal 需要 latest VersionRow,
 * 所以加一层异步 loader,点击后拉详情再渲染 Modal。
 */
function EditSkillLoader({
  name,
  onClose,
}: {
  name: string;
  onClose: () => void;
}) {
  const { data, isLoading, error } = usePackageDetail(name);
  if (isLoading) {
    return (
      <div className="modal-mask" onClick={onClose}>
        <div className="modal" onClick={(e) => e.stopPropagation()} style={{ padding: 24 }}>
          加载中…
        </div>
      </div>
    );
  }
  if (error || !data) {
    return (
      <div className="modal-mask" onClick={onClose}>
        <div className="modal" onClick={(e) => e.stopPropagation()} style={{ padding: 24 }}>
          <div className="err">{error ? (error as Error).message : "未找到该包"}</div>
          <div style={{ marginTop: 12, textAlign: "right" }}>
            <button className="btn btn-secondary" onClick={onClose}>关闭</button>
          </div>
        </div>
      </div>
    );
  }
  const latest = data.versions[0];
  if (!latest) {
    return (
      <div className="modal-mask" onClick={onClose}>
        <div className="modal" onClick={(e) => e.stopPropagation()} style={{ padding: 24 }}>
          <div className="err">该包暂无版本</div>
          <div style={{ marginTop: 12, textAlign: "right" }}>
            <button className="btn btn-secondary" onClick={onClose}>关闭</button>
          </div>
        </div>
      </div>
    );
  }
  return <EditSkillModal pkg={data.package} latest={latest} onClose={onClose} />;
}

/**
 * 管控状态徽章: 区分"未受管"和"受管(N 个 state)"。
 * - 未受管: 灰 — 平台不管,作者自负
 * - 受管 + enabled: 蓝/主色 — 平台管,当前正常
 * - 受管 + disabled: 黄 — 软停用
 * - 受管 + removed: 红 — 永久下架
 * - 受管 + deprecated: 灰 + 文字 — 不推荐
 */
function ManagedBadge({
  managed,
  state,
}: {
  managed: boolean;
  state: SkillState | null | undefined;
}) {
  if (!managed) {
    return <span className="pill pill-neutral">未受管</span>;
  }
  const stateLabel: Record<SkillState, string> = {
    enabled: "启用",
    disabled: "停用",
    removed: "下架",
    deprecated: "弃用",
  };
  const stateCls: Record<SkillState, string> = {
    enabled: "pill-primary",
    disabled: "pill-warning",
    removed: "pill-danger",
    deprecated: "pill-neutral",
  };
  const s = state ?? "enabled";
  return (
    <span className={`pill ${stateCls[s]}`}>
      受管·{stateLabel[s]}
    </span>
  );
}

function ChannelBadge({ channel }: { channel: string }) {
  const cls: Record<string, string> = {
    official: "pill-primary",
    community: "pill-neutral",
    private: "pill-warning",
  };
  const label: Record<string, string> = {
    official: "官方",
    community: "社区",
    private: "私有",
  };
  return (
    <span className={`pill ${cls[channel] ?? "pill-neutral"}`}>
      {label[channel] ?? channel}
    </span>
  );
}

function DeleteConfirmModal({
  name,
  busy,
  onCancel,
  onConfirm,
}: {
  name: string;
  busy: boolean;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  const [text, setText] = useState("");
  const canConfirm = text === name;
  return (
    <div className="modal-mask" onClick={busy ? undefined : onCancel}>
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        style={{ display: "flex", flexDirection: "column", gap: 14, width: 420 }}
      >
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div
            style={{
              width: 40,
              height: 40,
              borderRadius: 10,
              background: "var(--danger-soft)",
              color: "var(--danger)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 20,
            }}
          >
            ⚠
          </div>
          <div>
            <h2 style={{ fontSize: 17, margin: 0, fontWeight: 600 }}>彻底删除这个包?</h2>
            <p className="page-sub" style={{ marginTop: 2 }}>
              此操作不可恢复。下架(yank)可恢复,删除不行。
            </p>
          </div>
        </div>

        <label style={{ display: "flex", flexDirection: "column", gap: 5 }}>
          <span style={{ fontSize: 12, color: "var(--text-muted)" }}>
            输入包名 <code className="code-soft">{name}</code> 确认:
          </span>
          <input
            value={text}
            onChange={(e) => setText(e.target.value)}
            autoFocus
            placeholder={name}
          />
        </label>

        <div style={{ display: "flex", gap: 10, marginTop: 4 }}>
          <button
            type="button"
            className="btn btn-secondary"
            style={{ flex: 1 }}
            onClick={onCancel}
            disabled={busy}
          >
            取消
          </button>
          <button
            type="button"
            className="btn btn-danger"
            style={{ flex: 2 }}
            onClick={onConfirm}
            disabled={!canConfirm || busy}
          >
            {busy ? "删除中…" : "确认彻底删除"}
          </button>
        </div>
      </div>
    </div>
  );
}
